import { useState } from "react"
import ReactCanvasConfetti from "react-canvas-confetti"
import { animated, useTransition } from "react-spring"
import { useDrag } from "react-use-gesture"

import { colors } from "@src/utilities/confetti"
import classNames from "@src/utilities/class-names"
import { formatCurrency } from "@src/utilities/format-numbers"
import siteUrl from "@src/utilities/get-site-url"
import optimizeImage from "@src/utilities/optimize-image"

import Button from "@src/components/Button"
import ProductHeaderLinks from "@src/components/ProductHeaderLinks"

const defaultTransition = {}

export interface BundleHeaderProps {
  dealerCodeInstructions: string | null
  dealerCodePageUrl: string | null
  featuredImages: string[]
  inStock: boolean
  isAddingToBag: boolean
  isReady: boolean
  onAddToCart: () => void
  productIconLinks?: any[]
  productLongDescription: string
  productName?: string | null
  productPageUrl: string
  productShortDescription?: string | null
  productSku?: string
  wholesalePurchasePrice: number | null
  yotpoId?: string | null
}

export default function BundleHeader({
  dealerCodeInstructions,
  dealerCodePageUrl,
  featuredImages,
  inStock,
  isAddingToBag,
  isReady,
  onAddToCart,
  productIconLinks,
  productLongDescription,
  productName,
  productPageUrl,
  productShortDescription,
  productSku,
  wholesalePurchasePrice,
}: BundleHeaderProps) {
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0)

  const transitions = useTransition(
    featuredImages[currentSlideIndex],
    defaultTransition
  )

  const goToNextSlide = () =>
    setCurrentSlideIndex((prevState) =>
      prevState === featuredImages.length - 1 ? 0 : prevState + 1
    )

  const goToPreviousSlide = () =>
    setCurrentSlideIndex((prevState) =>
      prevState === 0 ? featuredImages.length - 1 : prevState - 1
    )

  const bindDrag = useDrag(
    ({ down, movement: [mx] }) => {
      if (down) {
        return
      }
      if (mx < 0) {
        goToPreviousSlide()
      } else {
        goToNextSlide()
      }
    },
    { axis: "x" }
  )

  return (
    <>
      <div className="mb-8 md:hidden" data-component="product-header">
        {transitions((springStyle, featuredImage, key) => (
          <animated.div
            {...bindDrag()}
            key={`slide-${key}`}
            style={springStyle}
          >
            <div
              className="bg-center bg-cover before:content-[''] before:block before:pt-[125%]"
              style={{
                backgroundImage: `url(${featuredImage})`,
              }}
            />
          </animated.div>
        ))}
      </div>
      <div className="container-desktop">
        <div className="md:flex md:items-start">
          <div className="hidden md:block md:flex-1 md:mb-16 lg:flex lg:flex-[3_0_0%] lg:flex-row">
            <div className="hidden items-start lg:block lg:pr-8">
              {featuredImages.map((image, idx) => (
                <div
                  className={classNames(
                    "bg-center bg-cover w-24 before:content-[''] before:block before:pt-[100%] cursor-pointer",
                    idx === 0 ? "" : "mt-2"
                  )}
                  key={`thumbnail_${idx}`}
                  onClick={() => setCurrentSlideIndex(idx)}
                  style={{
                    backgroundImage: `url(${optimizeImage(image, 300)})`,
                  }}
                />
              ))}
            </div>
            <div className="grow">
              {transitions((springStyle, featuredImage, key) => (
                <animated.div
                  {...bindDrag()}
                  key={`slide-${key}`}
                  style={springStyle}
                >
                  <div
                    className="bg-center bg-cover mr-8 before:content-[''] before:block before:pt-[125%]"
                    style={{
                      backgroundImage: `url(${optimizeImage(
                        featuredImage,
                        1600
                      )})`,
                    }}
                  />
                </animated.div>
              ))}
            </div>
          </div>
          <div className="mb-16 md:mt-8 md:flex-[1_0_0%] lg:flex-[2_0_0%]">
            <h1
              className="font-extrabold mb-1 text-2xl md:text-3xl lg:text-4xl uppercase"
              dangerouslySetInnerHTML={{
                __html: productName ?? "Enter a Product Name",
              }}
            />
            {productShortDescription && (
              <h2
                className="font-serif italic tracking-wide mb-2 text-lg"
                dangerouslySetInnerHTML={{ __html: productShortDescription }}
              />
            )}
            {productSku && (
              <div
                className="yotpo bottomLine"
                data-appkey={process.env.NEXT_PUBLIC_YOTPO_APP_KEY}
                data-domain={process.env.NEXT_PUBLIC_WEBSITE_BASE_URL}
                data-product-id={productSku}
                data-name={productName}
                data-url={`${siteUrl()}/${productPageUrl}`}
                data-description={productShortDescription}
              />
            )}
            <hr className="bg-gray-300 my-4" />
            <div
              className="prose font-serif prose-lg"
              dangerouslySetInnerHTML={{ __html: productLongDescription }}
            />
            {productIconLinks &&
              productIconLinks.length > 0 &&
              productIconLinks.map((link) => (
                <ProductHeaderLinks
                  currentSlug={productPageUrl}
                  header={link.header}
                  links={link.links}
                  linksKey={link._uid}
                />
              ))}
            <hr className="bg-gray-300 my-4" />
            {!inStock && (
              <div className="bg-red/20 border border-red font-browser font-bold p-4 text-red text-sm">
                This item is currently out of stock. Check back soon!
              </div>
            )}
            <div className="w-full max-w-[240px]">
              {dealerCodeInstructions && dealerCodePageUrl && (
                <div className="py-2">
                  <div className="font-browser pb-2 text-sm">
                    {dealerCodeInstructions}
                  </div>
                  <Button
                    className={["block", "w-full"]}
                    url={dealerCodePageUrl}
                    size="small"
                    text="Get Your Dealer Code"
                    type="borderBlack"
                  />
                </div>
              )}
              {inStock && wholesalePurchasePrice ? (
                <>
                  <div className="mt-4 mb-4">
                    <Button
                      className={["block", "w-full"]}
                      disabled={isAddingToBag || !isReady}
                      onClick={() => onAddToCart()}
                      size="small"
                      text={
                        isAddingToBag
                          ? "Adding to your cart"
                          : `Add to cart | ${formatCurrency(
                              wholesalePurchasePrice
                            )}`
                      }
                    />
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
        <ReactCanvasConfetti
          className="fixed h-full left-0 pointer-events-none top-0 w-full z-[999999]"
          colors={colors}
          fire={!isAddingToBag}
          particleCount={150}
          spread={360}
          startVelocity={30}
          ticks={360}
          zIndex={0}
        />
      </div>
    </>
  )
}
